import { Eye, Pause, Play, XCircle, Copy, RotateCcw } from "lucide-react"
import type { TableAction, BulkAction } from "@shared/types/data-table"
import type { NativeToken } from "@shared/types/native-token.types"
import { createBaseActions, mergeActions, type BaseActionHandlers } from "./base-actions"

interface NativeTokenSpecificHandlers {
  onViewDetails?: (token: NativeToken) => void
  onSuspend?: (token: NativeToken) => void
  onReactivate?: (token: NativeToken) => void
  onRevoke?: (token: NativeToken) => void
  onRefresh?: (token: NativeToken) => void
}

export type NativeTokenActionHandlers = BaseActionHandlers<NativeToken> & NativeTokenSpecificHandlers

const createNativeTokenBulkActions = (): BulkAction[] => [
  {
    key: "suspend",
    label: "Suspend",
    icon: <Pause className="h-4 w-4" />,
    onClick: (_ids) => {},
  },
  {
    key: "revoke",
    label: "Revoke",
    icon: <XCircle className="h-4 w-4" />,
    onClick: (_ids) => {},
  },
]

const createNativeTokenRowActions = (handlers: NativeTokenActionHandlers): TableAction<NativeToken>[] => [
  {
    key: "view-details",
    label: "View details",
    icon: <Eye className="h-4 w-4" />,
    onClick: (token) => handlers.onViewDetails?.(token),
  },
  {
    key: "suspend",
    label: "Suspend token",
    icon: <Pause className="h-4 w-4" />,
    onClick: (token) => handlers.onSuspend?.(token),
    condition: (token) => token.status === "active",
  },
  {
    key: "reactivate",
    label: "Reactivate token",
    icon: <Play className="h-4 w-4" />,
    onClick: (token) => handlers.onReactivate?.(token),
    condition: (token) => token.status === "suspended" || token.status === "inactive",
  },
  {
    key: "refresh",
    label: "Refresh token",
    icon: <RotateCcw className="h-4 w-4" />,
    onClick: (token) => handlers.onRefresh?.(token),
    condition: (token) => token.status === "active",
  },
  {
    key: "revoke",
    label: "Revoke token",
    icon: <XCircle className="h-4 w-4" />,
    onClick: (token) => handlers.onRevoke?.(token),
    condition: (token) => token.status !== "revoked",
  },
  {
    key: "copy-id",
    label: "Copy token ID",
    icon: <Copy className="h-4 w-4" />,
    onClick: (token) => handlers.onCopyId?.(token),
  },
]

export const getNativeTokenActions = (handlers: NativeTokenActionHandlers = {}) => {
  const baseActions = createBaseActions<NativeToken>('Native Token', handlers, {
    showEdit: false,
    showDelete: false,
    showFlag: false,
    showAddNote: false,
    showCopyId: false,
    showExport: true,
  })

  const specificActions = {
    bulkActions: createNativeTokenBulkActions(),
    rowActions: createNativeTokenRowActions(handlers),
  }

  return mergeActions(baseActions, specificActions)
}

export const getNativeTokenBulkActions = (handlers: NativeTokenActionHandlers = {}): BulkAction[] =>
  getNativeTokenActions(handlers).bulkActions

export const getNativeTokenRowActions = (_tokenData: NativeToken, handlers: NativeTokenActionHandlers = {}): TableAction<NativeToken>[] =>
  getNativeTokenActions(handlers).rowActions
